import { useState, useEffect } from "react";
import { Icon, cx, DataCard, Stat, Button } from "../components/ui";
import { api } from "../api/client";
import UsersPage from "../features/users/UsersPage";
import AdminStudentsPage from "../features/students/AdminStudentsPage";
import TrainerStudentsPage from "../features/students/TrainerStudentsPage";
import AttendancePage from "../features/attendance/AttendancePage";
import RecapPage from "../features/attendance/RecapPage";
import AdminSalaryPage from "../features/salary/AdminSalaryPage";
import SettingsPage from "../features/salary/SettingsPage";
import StaffPayrollPage from "../features/salary/StaffPayrollPage";
// New feature components
import DashboardAdmin from "../features/admin/DashboardAdmin";
import SalaryAdjustment from "../features/admin/SalaryAdjustment";
import EditRequestsPage from "../features/admin/EditRequestsPage";
import BranchesPage from "../features/admin/BranchesPage";
import MyPayslip from "../features/trainer/MyPayslip";
import ManagementDailyPage from "../features/management/ManagementDailyPage";
import PemantauanTrainerPage from "../features/management/PemantauanTrainerPage";
import TrainerSchedulePage from "../features/management/TrainerSchedulePage";
import RaportPage from "../features/raport/RaportPage";
import RaportTLPage from "../features/raport/tl/RaportTLPage";
import ClassesPage from "../features/classes/ClassesPage";
import MonitorPage from "../features/monitor/MonitorPage";
import { cabangUntukPeran } from "../utils/peranCabang";

// Label role (untuk switcher). Kunci kanonik = users.roles.
const ROLE_LABEL = {
  trainer: "Trainer",
  admin: "Admin",
  team_leader: "Team Leader",
  management: "Management"
};

function TrainerDashboard({ user, setView }) {
  return (
    <>
      <div className="mb-8">
        <h1 className="text-3xl font-black tracking-tight md:text-4xl">
          Welcome back, {user.nickname || user.name}
        </h1>
        <p className="mt-2 text-slate-500">
          This is your trainer dashboard. Use the shortcuts below to manage your students,
          attendance, recap, and payslip.
        </p>
      </div>

      <div className="mb-6 grid gap-4 md:grid-cols-4">
        <Stat label="Role" value={user.position || "Trainer"} primary />
        <Stat label="Email" value={user.email || "-"} />
        <Stat label="Nickname" value={user.nickname || "-"} />
        <Stat label="Status" value="Active" good />
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <TrainerShortcut
          icon="group"
          title="Student List"
          description="View students assigned to you."
          onClick={() => setView("trainer-students")}
        />

        <TrainerShortcut
          icon="calendar_today"
          title="Attendance"
          description="Submit daily attendance."
          onClick={() => setView("trainer-attendance")}
        />

        <TrainerShortcut
          icon="analytics"
          title="Monthly Recap"
          description="Review attendance history."
          onClick={() => setView("trainer-recap")}
        />

        <TrainerShortcut
          icon="receipt_long"
          title="My Payslip"
          description="View monthly salary record."
          onClick={() => setView("trainer-payslip")}
        />
      </div>
    </>
  );
}

function TrainerShortcut({ icon, title, description, onClick }) {
  return (
    <DataCard className="lift cursor-pointer p-5" onClick={onClick}>
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/15 text-primary">
        <Icon name={icon} />
      </div>

      <h3 className="text-lg font-black">{title}</h3>
      <p className="mt-2 min-h-[42px] text-sm text-slate-500">{description}</p>

      <Button className="mt-5 w-full" onClick={onClick}>
        Open
        <Icon name="arrow_forward" />
      </Button>
    </DataCard>
  );
}

function ManagementDashboard({ user }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-10 shadow-xl dark:border-primary/10 dark:bg-slate-900/30">
      <h1 className="text-3xl font-black">Management Dashboard</h1>
      <p className="mt-2 text-slate-500">
        Welcome, {user.name}. Management-specific reports will be connected in a later batch.
      </p>
    </div>
  );
}

export default function Shell({
  user,
  onLogout,
  setToast,
  theme,
  toggleTheme,
  activeBranch = null,
  activeRole = null,
  onSwitchBranch,
  onSwitchRole,
  onSelectBranch,
  onBukaCabang
}) {
  const isSuperAdmin = !!user.isSuperAdmin;
  const allBranches = user.branches || [];
  // Mode "Semua Cabang" (super admin, belum memfilter ke cabang tertentu) = hanya-baca.
  const isAllBranchesView = isSuperAdmin && !activeBranch;

  // Role login/akses akun (bisa MULTI). Portal ditentukan ROLE AKTIF (dipilih saat login /
  // via "Ganti Role"). Fallback: role pertama, lalu derivasi dari position (data lama).
  const roleKeys = Array.isArray(user.roles) ? user.roles : [];
  const derivedRole = (() => {
    const p = String(user.position || "").toLowerCase();
    if (/admin/.test(p)) return "admin";
    if (/management/.test(p)) return "management";
    if (/team\s*leader/.test(p) || p.trim() === "leader") return "team_leader";
    return "trainer";
  })();
  const role = activeRole || roleKeys[0] || derivedRole;
  const multiRole = roleKeys.length > 1;
  // "Ganti cabang" hanya bila PERAN AKTIF punya lebih dari satu cabang (peran per cabang:
  // team leader BR07 yang trainer di BR01 punya satu cabang di tiap portal).
  const multiBranch = cabangUntukPeran(user, role).length > 1;

  const isAdmin = role === "admin";
  const isManagement = role === "management";
  const isTeamLeader = role === "team_leader";
  const isTrainer = role === "trainer";
  // Staff non-trainer: tak mengajar, tak mengelola. Yang ia butuhkan cuma
  // melihat dan mengunduh slip gajinya sendiri.
  const isStaff = role === "staff";

  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Raport: menunya tampil hanya bila server menyalakan REPORTS_ENABLED. Gagal bertanya =
  // anggap mati — menu yang tak muncul lebih baik daripada menu yang berujung "belum aktif".
  const [raportAktif, setRaportAktif] = useState(false);
  const pakaiRaport = isTrainer || isTeamLeader;
  useEffect(() => {
    if (!pakaiRaport) return undefined;
    let batal = false;
    Promise.resolve(api.getRaportStatus())
      .then((r) => { if (!batal) setRaportAktif(!!(r && r.aktif)); })
      .catch(() => {});
    return () => { batal = true; };
  }, [pakaiRaport]);
  const MENU_RAPORT = new Set(["trainer-raport", "teamleader-raport"]);

  const defaultView = isAdmin
    ? "admin-dashboard"
    : isManagement
    ? "management-daily"
    : isTeamLeader
    ? "admin-dashboard"
    : isStaff
    ? "trainer-payslip"
    : "trainer-dashboard";

  const nav = isAdmin
    ? [
        ["admin-dashboard",    "dashboard",          "Dashboard"],
        ["admin-users",        "manage_accounts",    "User Management"],
        // Kelola Cabang hanya untuk Super Admin (operasi tingkat organisasi / Q7).
        ...(isSuperAdmin ? [["admin-branches", "store", "Kelola Cabang"]] : []),
        ["admin-students",     "group",              "All Students"],
        // Manajemen Kelas: admin + team leader (cabangnya). Server tetap membatasi
        // per cabang lewat loadClassScoped(); Backfill khusus super admin.
        ["admin-classes",      "school",             "Manajemen Kelas"],
        // Pemantauan Sistem: lintas SEMUA cabang -> super admin saja.
        // (Pemantauan Trainer sengaja TIDAK di sini: masa pengawasan urusan Management
        // Portal saja — keputusan 20 Okt 2026.)
        ...(isSuperAdmin ? [["admin-monitor", "monitor_heart", "Pemantauan Sistem"]] : []),
        // Trainer Schedule: jam mengajar & jam kosong lintas SEMUA cabang -> super admin saja
        // (rutenya requireManagementOrSuperAdmin). Admin cabang tidak.
        ...(isSuperAdmin ? [["management-schedule", "schedule", "Trainer Schedule"]] : []),
        ["admin-recap",        "analytics",          "Attendance Recap"],
        ["admin-salary",       "payments",           "Trainer Payroll"],
        // Staff Payroll: admin saja — rutenya requireAdmin, sebab menetapkan tarif
        // & mencentang sesi staff berarti menetapkan gaji sendiri bagi team leader.
        ["admin-staff-payroll","badge",              "Staff Payroll"],
        ["admin-settings",     "database",           "Data Settings"],
        ["admin-adjustments",  "tune",               "Salary Adjustments"],
        ["admin-edit-requests","lock_open",          "Edit Requests"],
        // Slip gaji SENDIRI — admin pun digaji (lewat Staff Payroll), jadi ia
        // berhak mengeceknya tanpa membuka payroll orang lain.
        ["trainer-payslip",    "receipt_long",       "My Payslip"]
      ]
    : isManagement
    ? [
        ["management-daily",     "calendar_view_month", "Absensi Harian"],
        ["management-pemantauan", "supervisor_account", "Pemantauan Trainer"],
        ["management-schedule",  "schedule",            "Trainer Schedule"],
        ["management-dashboard", "monitoring",          "Dashboard"]
      ]
    : isTeamLeader
    ? [
        ["admin-dashboard",   "dashboard",           "Dashboard"],
        ["management-daily",  "calendar_view_month", "Absensi Harian"],
        ["admin-classes",     "school",              "Manajemen Kelas"],
        ["admin-recap",       "analytics",           "Attendance Recap"],
        ["admin-salary",      "payments",            "Trainer Payroll"],
        ["admin-adjustments", "tune",                "Salary Adjustments"],
        // Raport cabang: tampil hanya bila REPORTS_ENABLED menyala (raportAktif).
        ["teamleader-raport", "fact_check",          "Raport"],
        // Team leader ada DUA jenis: yang digaji per sesi staff dan yang ikut
        // payroll trainer. Slipnya satu halaman yang sama untuk keduanya.
        ["trainer-payslip",   "receipt_long",        "My Payslip"]
      ]
    : isStaff
    ? [
        // Sengaja HANYA slip gaji. Staff tak mengajar, jadi menampilkan daftar
        // murid atau absensi cuma memberinya pintu yang tak pernah ia pakai —
        // dan pintu yang tak dipakai adalah pintu yang tak pernah diperiksa.
        ["trainer-payslip",    "receipt_long",   "My Payslip"]
      ]
    : [
        ["trainer-dashboard",  "dashboard",      "Dashboard"],
        ["trainer-students",   "group",          "Student List"],
        ["trainer-attendance", "calendar_today", "Attendance"],
        ["trainer-recap",      "analytics",      "Recap"],
        // Raport: menunya hanya TAMPIL bila server menyalakan REPORTS_ENABLED (lihat
        // raportAktif di bawah). Kuncinya tetap terdaftar supaya URL & refresh jalan; bila
        // fiturnya mati, halamannya sendiri yang bilang begitu.
        ["trainer-raport",     "assignment",     "Raport"],
        ["trainer-payslip",    "payments",       "My Payslip"]
      ];

  // ── Routing ringan: halaman aktif disinkron ke URL (History API) ──
  // Refresh tetap di halaman ini; tombol Back/Forward berpindah antar halaman. view =
  // segmen pertama path URL (mis. "/admin-users"). Role & cabang tetap persist via
  // localStorage (App), jadi cukup men-URL-kan `view`.
  const navKeys = new Set(nav.map((n) => n[0]));
  const pathView = () =>
    window.location.pathname.replace(/^\/+/, "").split("/")[0] || "";
  const [view, setViewState] = useState(() =>
    navKeys.has(pathView()) ? pathView() : defaultView
  );

  // Pindah halaman → dorong entri history baru (agar Back berfungsi).
  const setView = (next) => {
    if (!next || next === view) return;
    try {
      window.history.pushState({ view: next }, "", "/" + next);
    } catch (e) {
      /* abaikan (mis. sandbox tanpa history) */
    }
    setViewState(next);
  };

  // Rapikan URL awal agar cocok dgn view (mis. root "/" atau URL role lain → "/<view>").
  // replaceState (bukan push) supaya tak menambah entri history palsu saat load.
  useEffect(() => {
    if (pathView() !== view) {
      try {
        window.history.replaceState({ view }, "", "/" + view);
      } catch (e) {
        /* abaikan */
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Back/Forward → sinkronkan view dari URL.
  useEffect(() => {
    const onPop = () => {
      const p = pathView();
      setViewState(navKeys.has(p) ? p : defaultView);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shellLabel = isAdmin
    ? "Admin Console"
    : isManagement
    ? "Management Portal"
    : isTeamLeader
    ? "Team Leader Portal"
    : "Trainer Dashboard";

  const headerTitle = isAdmin
    ? "Admin Controls"
    : isManagement
    ? "Management Overview"
    : isTeamLeader
    ? "Team Leader"
    : "Trainer Workspace";

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Backdrop (mobile) */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={cx(
          "fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-slate-200 bg-white/95 backdrop-blur transition-transform duration-200 dark:border-primary/20 dark:bg-slate-900/95",
          "md:static md:z-auto md:translate-x-0 md:bg-white/80 md:dark:bg-slate-900/60",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex items-center gap-3 p-6">
          <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-xl bg-white ring-1 ring-slate-200 dark:ring-primary/20">
            <img
              src={process.env.PUBLIC_URL + "/logo-kip.jpeg"}
              alt="KIP"
              className="h-full w-full object-contain"
            />
          </div>

          <div>
            <h1 className="text-xl font-black text-primary">KIP Portal</h1>
            <p className="text-xs text-slate-500">{shellLabel}</p>
          </div>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto px-4 py-2">
          {nav.filter((item) => !MENU_RAPORT.has(item[0]) || raportAktif || view === item[0]).map((item) => (
            <button
              key={item[0]}
              onClick={() => {
                setView(item[0]);
                setSidebarOpen(false);
              }}
              className={cx(
                "relative flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-bold transition hover:translate-x-0.5",
                view === item[0]
                  ? "bg-primary/15 text-primary"
                  : "text-slate-500 hover:bg-slate-100 hover:text-primary dark:hover:bg-primary/10"
              )}
            >
              {/* Batang penanda di tepi kiri menu yang sedang dibuka. Ia lahir
                  bersama tombolnya, jadi memanjang tiap kali halaman berganti. */}
              {view === item[0] && (
                <span
                  aria-hidden="true"
                  className="anim-bar absolute left-0 top-1/2 h-6 w-1 rounded-r bg-primary"
                />
              )}
              <Icon name={item[1]} />
              {item[2]}
            </button>
          ))}
        </nav>

        <div className="border-t border-slate-200 p-4 dark:border-primary/10">
          {/* Ganti Role — akun dgn >1 role (mis. team leader yang juga mengajar). */}
          {multiRole && (
            <div className="mb-3 rounded-xl border border-slate-200 p-3 dark:border-primary/15">
              <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                Masuk sebagai
              </p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {roleKeys.map((r) => (
                  <button
                    key={r}
                    onClick={() => {
                      if (r !== role) {
                        setSidebarOpen(false);
                        onSwitchRole && onSwitchRole(r);
                      }
                    }}
                    className={cx(
                      "rounded-lg px-2.5 py-1 text-xs font-bold transition",
                      r === role
                        ? "bg-primary text-white shadow shadow-primary/30"
                        : "border border-slate-200 text-slate-500 hover:border-primary/40 dark:border-slate-700"
                    )}
                  >
                    {ROLE_LABEL[r] || r}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Cabang aktif + Ganti — di sidebar agar mudah diakses di MOBILE (buka menu). */}
          {!isSuperAdmin && activeBranch && (
            <div className="mb-3 rounded-xl border border-slate-200 p-3 dark:border-primary/15">
              <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                Cabang Aktif
              </p>
              <div className="mt-1 flex items-center justify-between gap-2">
                <span className="flex min-w-0 items-center gap-1.5 text-sm font-black text-primary">
                  <Icon name="store" className="text-base" />
                  <span className="truncate" title={activeBranch.name || activeBranch.code}>
                    {activeBranch.code}
                    {activeBranch.name ? ` · ${activeBranch.name}` : ""}
                  </span>
                </span>
                {multiBranch && (
                  <button
                    onClick={() => {
                      setSidebarOpen(false);
                      onSwitchBranch && onSwitchBranch();
                    }}
                    className="flex shrink-0 items-center gap-1 rounded-lg border border-primary/40 px-3 py-1.5 text-xs font-bold text-primary transition hover:bg-primary/10"
                  >
                    <Icon name="swap_horiz" className="text-sm" />
                    Ganti
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 rounded-xl bg-primary/5 p-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary font-black text-white">
              {(user.name || "U")[0].toUpperCase()}
            </div>

            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-black">{user.name}</p>
              <p className="truncate text-xs text-slate-500">
                {ROLE_LABEL[role] || user.position || "User"}
              </p>
            </div>

            <button
              onClick={onLogout}
              className="text-slate-400 hover:text-red-500"
            >
              <Icon name="logout" />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-16 items-center justify-between gap-3 border-b border-slate-200 bg-white/60 px-4 backdrop-blur dark:border-primary/20 dark:bg-background-dark/70 sm:px-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen((v) => !v)}
              className="rounded-xl p-2 hover:bg-slate-100 dark:hover:bg-primary/10 md:hidden"
            >
              <Icon name="menu" className="text-primary" />
            </button>

            <h2 className="font-black">{headerTitle}</h2>
          </div>

          <div className="flex items-center gap-3">
            {/* Super Admin: dropdown filter cabang (Semua + tiap cabang) */}
            {isSuperAdmin ? (
              <div className="flex items-center gap-1.5">
                <Icon name="filter_alt" className="text-sm text-primary" />
                <select
                  value={activeBranch?.id ?? ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (!val) return onSelectBranch && onSelectBranch(null);
                    const b = allBranches.find((x) => String(x.id) === val);
                    onSelectBranch && onSelectBranch(b || null);
                  }}
                  title="Filter cabang"
                  className="max-w-[12rem] rounded-lg border border-slate-200 bg-white/80 px-2 py-1 text-xs font-bold text-slate-600 focus:border-primary focus:outline-none dark:border-primary/20 dark:bg-slate-900/70 dark:text-slate-300"
                >
                  <option value="">Semua Cabang</option>
                  {allBranches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.code}
                      {b.name ? ` · ${b.name}` : ""}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              /* Non-super: badge cabang aktif + ganti (bila tergabung >1 cabang) */
              activeBranch && (
                <div className="flex items-center gap-1.5">
                  <span
                    className="hidden items-center gap-1 rounded-lg bg-primary/10 px-2.5 py-1 text-xs font-bold text-primary sm:inline-flex"
                    title={activeBranch.name || activeBranch.code}
                  >
                    <Icon name="store" className="text-sm" />
                    <span className="max-w-[10rem] truncate">
                      {activeBranch.code}
                      {activeBranch.name ? ` · ${activeBranch.name}` : ""}
                    </span>
                  </span>

                  {multiBranch && (
                    <button
                      onClick={onSwitchBranch}
                      title="Ganti cabang"
                      className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-bold text-slate-500 hover:border-primary/40 hover:text-primary dark:border-primary/20"
                    >
                      <Icon name="swap_horiz" className="text-sm sm:mr-1 sm:align-middle" />
                      <span className="hidden sm:inline">Ganti</span>
                    </button>
                  )}
                </div>
              )
            )}

            {/* Link Materi pembelajaran (aplikasi terpisah) — untuk SEMUA jenis akun. */}
            <a
              href="https://srv2091961.hstgr.cloud/materials/"
              target="_blank"
              rel="noopener noreferrer"
              title="Buka materi pembelajaran"
              className="inline-flex items-center gap-2 rounded-xl bg-primary px-3 py-2 text-sm font-bold text-white transition hover:bg-primary/90"
            >
              <Icon name="menu_book" />
              <span className="hidden sm:inline">Materi</span>
            </a>

            <button
              onClick={toggleTheme}
              title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              className="rounded-xl p-2 text-slate-500 hover:bg-slate-100 hover:text-primary dark:hover:bg-primary/10"
            >
              <Icon name={theme === "dark" ? "light_mode" : "dark_mode"} />
            </button>

            <div className="hidden max-w-[30vw] truncate text-sm text-slate-500 lg:block">
              {user.email}
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6 md:p-8">
          {/* Pembungkus ber-KEY: tiap pindah halaman, React membuang simpul lama
              dan memasang yang baru, jadi animasinya terputar lagi. Tanpa key,
              simpulnya dipakai ulang dan halaman berganti tanpa gerak.
              MEMUDAR SAJA, sengaja tanpa transform. Dua alasan, keduanya dari
              kejadian nyata:
                1. Leluhur ber-transform jadi containing block bagi turunan
                   `position: fixed` — itu yang membuat dialog nyangkut di dasar
                   halaman. Dialog kini memakai portal, tapi transform di sini
                   tetap tak layak dipertaruhkan untuk seluruh isi halaman.
                2. Menggeser sub-pohon sebesar ini membuat peramban menyusun
                   lapisan komposit besar, dan isinya sempat tertinggal pudar
                   sampai ada guliran yang memaksa gambar ulang. */}
          <div key={view} className="anim-fade">
          {/* Banner mode "Semua Cabang" (super admin belum memfilter) — hanya-baca */}
          {isAllBranchesView && view !== "admin-branches" && view !== "admin-users" && (
            <div className="mb-4 flex items-start gap-3 rounded-xl border border-primary/20 bg-primary/5 p-3 text-sm text-primary">
              <Icon name="info" className="mt-0.5 text-base" />
              <p>
                <span className="font-black">Mode Semua Cabang</span> — tampilan gabungan
                lintas cabang (hanya-baca). Pilih cabang tertentu di kanan atas untuk
                menambah atau mengedit data.
              </p>
            </div>
          )}

          {/* ── Admin views ── */}
          {view === "admin-dashboard" && (
            <DashboardAdmin
              setToast={setToast}
              title={isTeamLeader ? "Dashboard Jadwal" : "Admin Dashboard"}
            />
          )}

          {view === "admin-branches" && isSuperAdmin && (
            <BranchesPage setToast={setToast} />
          )}

          {view === "admin-users" && (
            <UsersPage
              user={user}
              activeBranch={activeBranch}
              setToast={setToast}
            />
          )}

          {view === "admin-monitor" && isSuperAdmin && (
            <MonitorPage setToast={setToast} />
          )}

          {view === "admin-classes" && (
            <ClassesPage user={user} activeBranch={activeBranch} setToast={setToast} />
          )}

          {view === "admin-students" && (
            <AdminStudentsPage setToast={setToast} activeBranch={activeBranch} />
          )}

          {view === "admin-recap" && (
            <RecapPage admin setToast={setToast} />
          )}

          {view === "admin-salary" && (
            <AdminSalaryPage setToast={setToast} isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} />
          )}

          {view === "admin-staff-payroll" && (
            <StaffPayrollPage setToast={setToast} isSuperAdmin={isSuperAdmin} />
          )}

          {view === "admin-settings" && (
            <SettingsPage setToast={setToast} />
          )}

          {/* Salary adjustments */}
          {view === "admin-adjustments" && (
            <SalaryAdjustment setToast={setToast} isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} />
          )}

          {view === "admin-edit-requests" && (
            <EditRequestsPage user={user} setToast={setToast} />
          )}

          {/* ── Trainer views ── */}
          {view === "trainer-dashboard" && (
            <TrainerDashboard user={user} setView={setView} />
          )}

          {view === "trainer-students" && (
            <TrainerStudentsPage user={user} setToast={setToast} />
          )}

          {view === "trainer-attendance" && (
            <AttendancePage user={user} setToast={setToast} onBukaCabang={onBukaCabang} />
          )}

          {view === "trainer-recap" && (
            <RecapPage user={user} setToast={setToast} />
          )}

          {view === "trainer-raport" && (
            <RaportPage setToast={setToast} />
          )}

          {view === "teamleader-raport" && (
            <RaportTLPage setToast={setToast} />
          )}

          {/* Payslip */}
          {view === "trainer-payslip" && (
            <MyPayslip user={user} setToast={setToast} />
          )}

          {/* ── Management ── */}
          {view === "management-daily" && (
            <ManagementDailyPage user={user} role={role} setToast={setToast} />
          )}

          {view === "management-pemantauan" && (
            <PemantauanTrainerPage setToast={setToast} />
          )}

          {view === "management-schedule" && (
            <TrainerSchedulePage setToast={setToast} />
          )}

          {view === "management-dashboard" && (
            <ManagementDashboard user={user} />
          )}

          {/* ── Unknown role fallback ── */}
          {!isAdmin && !isTrainer && !isManagement && !isTeamLeader && !isStaff && (
            <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-700 dark:border-red-500/20 dark:bg-red-500/10">
              <h1 className="text-xl font-black">Unknown Role</h1>
              <p className="mt-2 text-sm">
                This account position is not recognized: {user.position || "Empty"}.
              </p>
            </div>
          )}
          </div>
        </div>
      </main>
    </div>
  );
}